/* eslint-disable import/no-cycle */
import * as axiosMainInstance from "axios";
import handleSuccess from "./handleSuccess";
import handleErrors from "./handleError";

const axios = axiosMainInstance.default.create();
axios.interceptors.response.use(handleSuccess, handleErrors);

export function createHeader({ accessToken, headers = {} }) {
  const header = {
    "Content-Type": "application/json",
    // 'Access-Control-Allow-Origin': '*',
    ...headers,
  };
  if (accessToken) {
    header["X-Auth-Token"] = `${accessToken}`;
  }
  return header;
}

function genCancelToken() {
  let cancel;
  const cancelToken = new axiosMainInstance.default.CancelToken((c) => {
    cancel = c;
  });
  return { cancel, cancelToken };
}

function createFileFormData(data) {
  const body = new FormData();
  Object.keys(data).forEach((key) => {
    body.append(key, data[key]);
  });

  return body;
}

/**
 * @typedef BaseApiParams
 * @type {object}
 * @property {any} data - data to be sent through api
 * @property {string} accessToken - access token to be sent
 * @property {string} endPoint -
 */

/**
 * @typedef ApiResponse
 * @type {object}
 * @property {object} responseObject - can be null
 * @property {number} code
 * @property {string} message
 */

/**
 * @typedef MultipartExtraParam
 * @type {object}
 * @property {function} onUploadProgress
 */

/**
 * @typedef MultipartApiParams
 * @type {MultipartExtraParam & BaseApiParams}
 */

/**
 * @param {string} url - Url endpoint
 * @param {BaseApiParams} apiParams - Api Parameters
 * @return {Promise}
 */
export function post(url, { data, accessToken, params, endPoint = "" }) {
  const { cancel, cancelToken } = genCancelToken();

  const request = axios.post(endPoint + url, data, {
    params,
    cancelToken,
    headers: createHeader({ accessToken }),
  });
  request.cancel = cancel;
  return request;
}
/**
 * @param {string} url - Url endpoint
 * @param {MultipartApiParams}
 * @param {string} endPoint - Optional base url
 * @return {Promise}
 */
export function multipart(
  url,
  { data, onUploadProgress = () => {}, accessToken, endPoint }
) {
  const { cancel, cancelToken } = genCancelToken();
  const body = createFileFormData(data);
  const request = axios.post(endPoint + url, body, {
    cancelToken,
    headers: createHeader({
      accessToken,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
    onUploadProgress: (progressEvent) => {
      onUploadProgress(progressEvent);
    },
  });
  request.cancel = cancel;
  return request;
}

/**
 * @param {string} url - Url endpoint
 * @param {BaseApiParams} apiParams - Api Parameters
 * @return {Promise<ApiResponse>}
 */
export function get(url, { data, accessToken, endPoint = "" }) {
  const { cancel, cancelToken } = genCancelToken();
  const request = axios.get(endPoint + url, {
    cancelToken,
    headers: createHeader({ accessToken }),
    params: data,
  });

  request.cancel = cancel;
  return request;
}

export function getBlob(url, { data, accessToken, endPoint = "" }) {
  const { cancelToken } = genCancelToken();
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    try {
      const response = await axios.get(endPoint + url, {
        responseType: "blob",
        cancelToken,
        headers: createHeader({ accessToken }),
        params: data,
      });
      const reader = new window.FileReader();
      reader.readAsDataURL(response);
      // eslint-disable-next-line func-names
      reader.onload = function () {
        resolve(reader.result);
      };
    } catch (e) {
      reject(e);
    }
  });
}

/**
 * @param {string} url - Url endpoint
 * @param {BaseApiParams} apiParams - Api Parameters
 * @return {Promise<ApiResponse>}
 */
export function deleteCall(url, { data, params, accessToken, endPoint = "" }) {
  const { cancel, cancelToken } = genCancelToken();
  const request = axios.delete(endPoint + url, {
    data,
    cancelToken,
    headers: createHeader({ accessToken }),
    params,
  });
  request.cancel = cancel;
  return request;
}

/**
 * @param {string} url - Url endpoint
 * @param {BaseApiParams} apiParams - Api Parameters
 * @return {Promise}
 */
export function put(url, { data, accessToken, params, endPoint = "" }) {
  const { cancel, cancelToken } = genCancelToken();
  const request = axios.put(endPoint + url, data, {
    params,
    cancelToken,
    headers: createHeader({ accessToken }),
  });
  request.cancel = cancel;
  return request;
}

/**
 * @param {string} url - Url endpoint
 * @param {BaseApiParams} apiParams - Api Parameters
 * @return {Promise<ApiResponse>}
 */
export function retryGet(url, { data: apiData, accessToken, endPoint = "" }) {
  const { retryConfig = {}, ...data } = apiData;
  const {
    retries,
    retryDelay,
    delayInitialRequest,
    passCondition,
    failCondition,
  } = {
    retries: 1000,
    retryDelay: 0,
    delayInitialRequest: false,
    passCondition: ({ code }) => code === 200,
    failCondition: ({ code }) => code === 404,
    ...retryConfig,
  };
  const { cancel, cancelToken } = genCancelToken();
  let retryAttemptsMade = 0;
  let response = {};
  const request = new Promise((resolve, reject) => {
    // eslint-disable-next-line no-use-before-define
    const timeoutRetry = (delay) => setTimeout(retryFunc, delay);
    async function retryFunc() {
      if (retryAttemptsMade < retries) {
        try {
          // eslint-disable-next-line no-plusplus
          retryAttemptsMade++;
          if (process.env.NODE_ENV === "development")
            response = await axios.get(endPoint + url, {
              cancelToken,
              headers: createHeader({ accessToken }),
              params: data,
            });
          if (passCondition(response)) {
            resolve(response);
          } else if (failCondition(response)) {
            reject(response);
          } else {
            timeoutRetry(retryDelay);
          }
        } catch (e) {
          reject(e);
        }
      } else {
        reject(response);
      }
    }
    timeoutRetry(delayInitialRequest ? retryDelay : 0);
  });
  request.cancel = cancel;
  return request;
}

/**
 * @param {string} url - Url endpoint
 * @param {BaseApiParams} apiParams - Api Parameters
 * @return {Promise}
 */
export function patch(url, { data, accessToken, params, endPoint = "" }) {
  const { cancel, cancelToken } = genCancelToken();
  const request = axios.patch(endPoint + url, data, {
    params,
    cancelToken,
    headers: createHeader({ accessToken }),
  });
  request.cancel = cancel;
  return request;
}
